import { Component } from '@theme/component';

/**
 * Tabbed sibling looks for the shop-the-look section.
 *
 * Panels are rendered server-side by `_look-tab` blocks (good for SEO and for the
 * no-JS fallback); this component only hydrates the tab rail and toggles which
 * panel is active. Nothing is cloned or re-parented, so the pins inside each
 * `look-media` keep their own component bindings and event listeners.
 *
 * Expected structure:
 * <shop-the-look-tabs>
 *   <div data-rail role="tablist"></div>          <!-- hydrated from panel templates -->
 *   <div data-panels>
 *     <article data-panel data-tab-id="…" data-active="true">
 *       <template data-rail-button><button data-tab-id="…">LOOK 01</button></template>
 *       …
 *     </article>
 *   </div>
 * </shop-the-look-tabs>
 */
export class ShopTheLookTabs extends Component {
  #abort = new AbortController();

  /** @type {string|null} */
  #activeTabId = null;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abort;

    const rail = this.querySelector('[data-rail]');

    // With a single look there is no rail — the panel just renders as a plain look.
    if (rail) {
      for (const panel of this.#panels) {
        const template = panel.querySelector('template[data-rail-button]');
        if (template instanceof HTMLTemplateElement) {
          rail.appendChild(template.content.cloneNode(true));
        }
      }

      for (const trigger of this.#triggers) {
        trigger.addEventListener('click', this.#onClick, { signal });
        trigger.addEventListener('keydown', this.#onKeydown, { signal });
      }
    }

    this.#activeTabId =
      this.#panels.find((panel) => panel.dataset.active === 'true')?.dataset.tabId ??
      this.#panels[0]?.dataset.tabId ??
      null;

    // Reveal the panels only once hydration succeeded. Until then CSS shows every
    // panel stacked, so a JS failure degrades to "all looks visible" rather than
    // "one look visible and no way to reach the others".
    this.dataset.hydrated = 'true';

    if (this.#activeTabId) this.#activate(this.#activeTabId, { force: true });

    // In the theme editor, selecting a block inside an inactive panel would otherwise
    // scroll to something the merchant cannot see. Bring its panel forward instead.
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
  }

  /** @returns {HTMLElement[]} */
  get #panels() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-panel]')));
  }

  /** @returns {HTMLButtonElement[]} */
  get #triggers() {
    return /** @type {HTMLButtonElement[]} */ (
      Array.from(this.querySelectorAll('button.shop-the-look-tabs__tab[data-tab-id]'))
    );
  }

  /**
   * @param {string} tabId
   * @returns {HTMLButtonElement|null}
   */
  #triggerFor(tabId) {
    return this.querySelector(`button.shop-the-look-tabs__tab[data-tab-id="${CSS.escape(tabId)}"]`);
  }

  /**
   * Shows the given look and marks its tab selected. Hard swap — no crossfade,
   * matching the reference design.
   *
   * @param {string|null} tabId
   * @param {{focus?: boolean, force?: boolean}} [options]
   */
  #activate(tabId, { focus = false, force = false } = {}) {
    if (!tabId) return;
    if (tabId === this.#activeTabId && !force) return;

    for (const trigger of this.#triggers) {
      const isActive = trigger.dataset.tabId === tabId;
      trigger.setAttribute('aria-selected', String(isActive));
      trigger.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const panel of this.#panels) {
      panel.dataset.active = String(panel.dataset.tabId === tabId);
    }

    this.#activeTabId = tabId;

    if (focus) this.#triggerFor(tabId)?.focus();
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const tabId = /** @type {HTMLElement} */ (event.currentTarget)?.dataset.tabId;
    if (tabId) this.#activate(tabId);
  };

  /**
   * WAI-ARIA tabs keyboard pattern: arrows move and activate, Home/End jump to the ends.
   *
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    const triggers = this.#triggers;
    if (triggers.length === 0) return;

    const currentIndex = triggers.findIndex((trigger) => trigger === event.currentTarget);
    if (currentIndex === -1) return;

    /** @type {number|null} */
    let nextIndex = null;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % triggers.length;
    else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + triggers.length) % triggers.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = triggers.length - 1;

    if (nextIndex == null) return;

    event.preventDefault();
    this.#activate(triggers[nextIndex]?.dataset.tabId ?? null, { focus: true });
  };

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    const target = /** @type {CustomEvent} */ (event).detail?.blockId
      ? this.querySelector(`[data-block-id="${CSS.escape(/** @type {CustomEvent} */ (event).detail.blockId)}"]`)
      : /** @type {HTMLElement|null} */ (/** @type {CustomEvent} */ (event).target);

    const panel = target instanceof Element ? target.closest('[data-panel]') : null;
    if (panel instanceof HTMLElement && this.contains(panel)) {
      this.#activate(panel.dataset.tabId ?? null);
    }
  };
}

if (!customElements.get('shop-the-look-tabs')) {
  customElements.define('shop-the-look-tabs', ShopTheLookTabs);
}
